import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Check, Pencil, MapPin, Sparkles, Target, X, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import TrophyUnlockCeremony from './TrophyUnlockCeremony';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'travel', label: 'Travel', color: '#A2D2FF', emoji: '✈️' },
  { id: 'skills', label: 'Skills', color: '#B9FBC0', emoji: '🎓' },
  { id: 'adventure', label: 'Adventure', color: '#FFC8DD', emoji: '🏞️' },
  { id: 'relationships', label: 'Relationships', color: '#FF6B6B', emoji: '💖' },
  { id: 'health', label: 'Health', color: '#FDF0D5', emoji: '🌱' },
  { id: 'creative', label: 'Creative', color: '#CDB4DB', emoji: '🎨' },
  { id: 'career', label: 'Career', color: '#FFE8A3', emoji: '💼' },
  { id: 'other', label: 'Other', color: '#E0E0E0', emoji: '✨' },
];

const EMOJI_SUGGESTIONS = ['✨', '🌍', '🏔️', '🏝️', '🎤', '📚', '🖌️', '🎸', '🚀', '🤿', '🛶', '🎿', '🐬', '🏇', '🎬', '👨‍🍳', '🌋', '💍', '🦉', '🌌'];

function CategoryChip({ cat, active, onClick }) {
  const c = CATEGORIES.find((x) => x.id === cat);
  if (!c) return null;
  return (
    <button
      onClick={onClick}
      className={`neo-btn px-3 py-1.5 text-xs font-bold whitespace-nowrap ${active ? 'ring-2 ring-black' : ''}`}
      style={{ backgroundColor: c.color }}
      data-testid={`bucket-cat-${c.id}`}
    >
      <span className="mr-1">{c.emoji}</span>{c.label}
    </button>
  );
}

function EditorModal({ initial, onSave, onClose }) {
  const [title, setTitle] = useState(initial?.title || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [emoji, setEmoji] = useState(initial?.emoji || '✨');
  const [category, setCategory] = useState(initial?.category || 'adventure');
  const [targetDate, setTargetDate] = useState(initial?.target_date || '');
  const [reflection, setReflection] = useState(initial?.reflection || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim()) {
      toast.error('Give your dream a name 🌟');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        description: description.trim(),
        emoji,
        category,
        target_date: targetDate || null,
        reflection,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="bucket-editor-modal"
    >
      <div
        className="neo-card bg-white dark:bg-[#252545] max-w-lg w-full max-h-[90vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-2xl font-bold font-['Fredoka']">
            {initial?.id ? 'Edit dream' : 'New dream ✨'}
          </h3>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white" data-testid="bucket-editor-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <label className="block mb-3">
          <span className="text-xs font-bold block mb-1">Title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. See the northern lights"
            maxLength={120}
            className="w-full px-3 py-2 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54]"
            data-testid="bucket-title-input"
          />
        </label>

        <label className="block mb-3">
          <span className="text-xs font-bold block mb-1">Why / details (optional)</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="What does this mean to you?"
            className="w-full px-3 py-2 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54] resize-none"
            data-testid="bucket-desc-input"
          />
        </label>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <label className="block">
            <span className="text-xs font-bold block mb-1">Category</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54]"
              data-testid="bucket-category-select"
            >
              {CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>{c.emoji} {c.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-bold block mb-1">Target date (optional)</span>
            <input
              type="date"
              value={targetDate || ''}
              onChange={(e) => setTargetDate(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54]"
              data-testid="bucket-target-date-input"
            />
          </label>
        </div>

        <div className="mb-3">
          <span className="text-xs font-bold block mb-1">Icon</span>
          <div className="flex flex-wrap gap-1.5">
            {EMOJI_SUGGESTIONS.map((e) => (
              <button
                key={e}
                onClick={() => setEmoji(e)}
                className={`w-9 h-9 rounded-xl border-2 border-black text-lg ${emoji === e ? 'bg-[#A2D2FF] scale-110' : 'bg-white hover:bg-[#FDF0D5]'}`}
              >
                {e}
              </button>
            ))}
          </div>
        </div>

        {initial?.completed && (
          <label className="block mb-3">
            <span className="text-xs font-bold block mb-1">Reflection (when you did it)</span>
            <textarea
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="How was it? Any memories you want to keep?"
              className="w-full px-3 py-2 rounded-xl border-2 border-black text-sm bg-[#FFFAF0] dark:bg-[#1f1f3a] resize-none"
              data-testid="bucket-reflection-input"
            />
          </label>
        )}

        <button
          onClick={save}
          disabled={saving}
          className="neo-btn w-full py-3 bg-[#B9FBC0] font-bold text-base disabled:opacity-50"
          data-testid="bucket-save-btn"
        >
          {saving ? 'Saving...' : initial?.id ? 'Save changes' : 'Add to bucket list ✨'}
        </button>
      </div>
    </div>
  );
}

function BucketCard({ item, onToggle, onEdit, onDelete, dragProps, isDragging, isOver }) {
  const cat = CATEGORIES.find((c) => c.id === item.category) || CATEGORIES[CATEGORIES.length - 1];
  return (
    <div
      {...(dragProps || {})}
      className={`neo-card p-4 relative transition-all ${item.completed ? 'opacity-80' : ''} ${isDragging ? 'opacity-40 scale-95' : ''} ${isOver ? 'ring-2 ring-pink-400' : ''}`}
      style={{ backgroundColor: cat.color + (item.completed ? '60' : '') }}
      data-testid={`bucket-item-${item.id}`}
    >
      <div className="flex items-start gap-3">
        {dragProps && (
          <GripVertical className="w-4 h-4 mt-3 text-black/30 cursor-grab active:cursor-grabbing flex-shrink-0" />
        )}
        <button
          onClick={() => onToggle(item)}
          className={`w-10 h-10 rounded-xl border-2 border-black flex items-center justify-center text-xl transition ${item.completed ? 'bg-green-400' : 'bg-white hover:bg-[#FFE8A3]'}`}
          data-testid={`bucket-toggle-${item.id}`}
          title={item.completed ? 'Mark as not done' : 'Mark as done'}
        >
          {item.completed ? <Check className="w-5 h-5" strokeWidth={3} /> : item.emoji}
        </button>
        <div className="flex-1 min-w-0">
          <p className={`font-bold font-['Fredoka'] text-base ${item.completed ? 'line-through opacity-70' : ''}`}>
            {item.title}
          </p>
          {item.description && <p className="text-xs opacity-80 mt-0.5">{item.description}</p>}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-black/80 text-white">
              {cat.emoji} {cat.label}
            </span>
            {item.target_date && (
              <span className="text-[10px] font-bold flex items-center gap-1 opacity-80">
                <Target className="w-3 h-3" /> {item.target_date}
              </span>
            )}
            {item.completed && item.completed_at && (
              <span className="text-[10px] font-bold text-green-700 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Done {item.completed_at.slice(0, 10)}
              </span>
            )}
          </div>
          {item.completed && item.reflection && (
            <p className="mt-2 text-xs italic bg-white/70 dark:bg-black/20 border border-black/20 rounded-lg p-2">
              💭 {item.reflection}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <button
            onClick={() => onEdit(item)}
            className="neo-btn p-1.5 bg-white"
            data-testid={`bucket-edit-${item.id}`}
            title="Edit"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onDelete(item)}
            className="neo-btn p-1.5 bg-[#FF6B6B] text-white"
            data-testid={`bucket-delete-${item.id}`}
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BucketList({ onUpdate }) {
  const [data, setData] = useState({ items: [], stats: { total: 0, completed: 0 } });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // item being edited
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState('all'); // all|active|done|<category>
  const [celebrateItem, setCelebrateItem] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [overId, setOverId] = useState(null);
  const fetchItems = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bucket-list`, { withCredentials: true });
      setData(data);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const saveNew = async (payload) => {
    try {
      await axios.post(`${API}/bucket-list`, payload, { withCredentials: true });
      toast.success('Added to your bucket list! ✨');
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const saveEdit = async (payload) => {
    try {
      await axios.patch(`${API}/bucket-list/${editing.id}`, payload, { withCredentials: true });
      toast.success('Updated!');
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const toggleDone = async (item) => {
    try {
      const next = !item.completed;
      const res = await axios.patch(`${API}/bucket-list/${item.id}`, { completed: next }, { withCredentials: true });
      if (next) {
        setCelebrateItem(res.data?.item || { ...item, completed: true });
        if (onUpdate) await onUpdate();
      } else {
        toast.info('Marked as not done');
      }
      fetchItems();
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await axios.delete(`${API}/bucket-list/${item.id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchItems();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  // Drag & drop handlers (only for the visible filtered list, disabled if filter hides items)
  const canReorder = filter === 'all';
  const onDragStart = (e, id) => {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', id); } catch { /* ignore */ }
  };
  const onDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!draggingId || draggingId === id) return;
    if (overId === id) return;
    setOverId(id);
    setData((prev) => {
      const arr = prev.items;
      const from = arr.findIndex((x) => x.id === draggingId);
      const to = arr.findIndex((x) => x.id === id);
      if (from === -1 || to === -1 || from === to) return prev;
      const next = arr.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return { ...prev, items: next };
    });
  };
  const onDragEnd = async () => {
    const was = draggingId;
    setDraggingId(null);
    setOverId(null);
    if (!was) return;
    try {
      const order = data.items.map((x) => x.id);
      await axios.post(`${API}/bucket-list/reorder`, { order }, { withCredentials: true });
    } catch {
      fetchItems();
    }
  };

  const filtered = data.items.filter((it) => {
    if (filter === 'all') return true;
    if (filter === 'active') return !it.completed;
    if (filter === 'done') return it.completed;
    return it.category === filter;
  });

  const progress = data.stats.total > 0 ? Math.round((data.stats.completed / data.stats.total) * 100) : 0;

  if (loading) {
    return <div className="neo-card p-8 text-center animate-pulse-soft">Loading your dreams...</div>;
  }

  return (
    <div className="space-y-4" data-testid="bucket-list-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold font-['Fredoka'] flex items-center gap-2">
            <Sparkles className="w-6 h-6" /> Bucket List
          </h2>
          <p className="text-xs opacity-75 mt-1">Dreams, goals, and one-day-I'll-do-it moments 💫</p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="neo-btn px-4 py-2 bg-[#FFC8DD] font-bold flex items-center gap-1.5"
          data-testid="bucket-add-btn"
        >
          <Plus className="w-4 h-4" /> Add dream
        </button>
      </div>

      {/* Progress banner */}
      <div
        className="neo-card p-4 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #FFE8A3 0%, #FFC8DD 100%)' }}
      >
        <div className="flex items-center gap-4">
          <div className="text-3xl">🌠</div>
          <div className="flex-1">
            <p className="text-xs font-bold uppercase tracking-wider">Your journey</p>
            <p className="text-lg font-bold font-['Fredoka']" data-testid="bucket-stats">
              {data.stats.completed} / {data.stats.total} dreams lived
            </p>
            <div className="mt-1 h-2 rounded-full bg-white/60 border-2 border-black overflow-hidden">
              <div className="h-full bg-green-500" style={{ width: `${progress}%` }} />
            </div>
          </div>
          <div className="text-2xl font-bold font-['Fredoka']">{progress}%</div>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'all', label: 'All', color: '#fff' },
          { id: 'active', label: '⏳ Active', color: '#FDF0D5' },
          { id: 'done', label: '✅ Done', color: '#B9FBC0' },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`neo-btn px-3 py-1.5 text-xs font-bold ${filter === f.id ? 'ring-2 ring-black' : ''}`}
            style={{ backgroundColor: f.color }}
            data-testid={`bucket-filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
        {CATEGORIES.map((c) => (
          <CategoryChip key={c.id} cat={c.id} active={filter === c.id} onClick={() => setFilter(c.id)} />
        ))}
      </div>

      {/* Items */}
      {filtered.length === 0 ? (
        <div className="neo-card p-8 text-center bg-white">
          <div className="text-5xl mb-2">🌠</div>
          <p className="font-bold font-['Fredoka']">
            {data.stats.total === 0 ? 'Your first dream awaits…' : 'Nothing in this view'}
          </p>
          <p className="text-xs opacity-75 mt-1">
            {data.stats.total === 0 ? 'Tap "Add dream" to start your bucket list!' : 'Try a different filter'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((item) => (
            <BucketCard
              key={item.id}
              item={item}
              onToggle={toggleDone}
              onEdit={setEditing}
              onDelete={remove}
              dragProps={canReorder && !item.completed ? {
                draggable: true,
                onDragStart: (e) => onDragStart(e, item.id),
                onDragOver: (e) => onDragOver(e, item.id),
                onDragEnd,
              } : null}
              isDragging={draggingId === item.id}
              isOver={overId === item.id && draggingId !== item.id}
            />
          ))}
        </div>
      )}

      {creating && (
        <EditorModal
          initial={null}
          onSave={saveNew}
          onClose={() => setCreating(false)}
        />
      )}
      {editing && (
        <EditorModal
          initial={editing}
          onSave={saveEdit}
          onClose={() => setEditing(null)}
        />
      )}

      {celebrateItem && (
        <TrophyUnlockCeremony
          item={celebrateItem}
          onClose={() => setCelebrateItem(null)}
        />
      )}
    </div>
  );
}
